import React, { useCallback, useEffect, useState } from "react";

import {
  ManifestIntegrationProvider,
  ManifestJSONSchema,
} from "src/api/connectApi/manifestTypes";
import { CodeEditor } from "src/base-components/CodeInput/CodeEditor";
import {
  FloatingWindowInternal,
  useFloatingWindowPosition,
} from "src/base-components/FloatingWindow/FloatingWindow";
import { FloatingWindowProps } from "src/base-components/FloatingWindow/store";
import { Button } from "src/design-system/Button";
import { Callout } from "src/design-system/Callout";
import { InputMappings } from "src/integrationNode/editorComponents/InputMappings";
import { InputMappings as InputMappingsType } from "src/integrationNode/types";
import { ValidationError } from "src/jsonSchema/validateJsonSchema";
import { parseAndValidateJsonSchema } from "src/manifestBuilder/schemaParser";
import { getDefaultManifestProviderResource } from "src/manifestConnectionNode/manifestConnectionResources/DefaultResources";
import { ManifestIntegrationResource } from "src/manifestConnectionNode/types";
import { fillInputMappingFromManifest } from "src/manifestConnectionNode/utils";
import { SchemaOptions } from "src/router/SearchParams";
import { SchemaConverter, SchemaUI } from "src/schema/schemaMappingUtils";
import { useManifestNodeBuilderWindow } from "src/schema/window/useManifestNodeBuilderWindow";
import { logger } from "src/utils/logger";

type ErrorDisplayProps = {
  error: ValidationError;
};

const ErrorDisplay: React.FC<ErrorDisplayProps> = ({ error }) => {
  const errorStyles: Record<ValidationError["type"], string> = {
    json: "border-red-500 bg-red-50",
    structure: "border-orange-500 bg-orange-50",
    refs: "border-yellow-500 bg-yellow-50",
    custom: "border-blue-500 bg-blue-50",
  };

  const errorIcons: Record<ValidationError["type"], string> = {
    json: "❌",
    structure: "⚠️",
    refs: "🔗",
    custom: "ℹ️",
  };

  const errorTitles: Record<ValidationError["type"], string> = {
    json: "JSON Syntax Error",
    structure: "Schema Structure Error",
    refs: "Reference Error",
    custom: "Validation Rule Violation",
  };

  const errorTips: Record<ValidationError["type"], string> = {
    json: "Tip: Check for missing commas, quotes, or brackets",
    structure: "Tip: Ensure your schema follows JSON Schema Draft 2020-12",
    refs: "Tip: All $ref references must point to valid definitions",
    custom: "Tip: Check the custom validation rules for your schema",
  };

  return (
    <div
      className={`rounded border-l-4 p-4 ${errorStyles[error.type]}`}
      data-loc="schema-error-display"
    >
      <div className="flex items-start gap-2">
        <span className="text-xl">{errorIcons[error.type]}</span>
        <div className="flex-1">
          <h4 className="mb-1 font-semibold text-sm">
            {errorTitles[error.type]}
          </h4>
          <p className="whitespace-pre-wrap font-mono text-sm">
            {error.message}
          </p>
          <p className="mt-2 text-gray-600 text-xs">{errorTips[error.type]}</p>
        </div>
      </div>
    </div>
  );
};

const DEFAULT_SCHEMA_EXAMPLE = `{
  "$schema": "https://json-schema.org/draft/2020-12/schema",
  "type": "object",
  "properties": {
    "field_name": {
      "type": [
        "string"
      ],
      "title": "Field name"
    }
  },
  "required": [
    "field_name"
  ],
  "sensitive": [],
  "order": [
    "field_name"
  ],
  "$defs": {}
}`;

const Title = () => {
  return (
    <div className="flex flex-col justify-self-start">
      <h3 className="font-inter-semibold-13px text-gray-800">
        Manifest Node Builder
      </h3>
      <p className="font-inter-normal-12px text-gray-500">
        Design and preview your input schema with a live UI
      </p>
    </div>
  );
};

/**
 * Creates a mock manifest from a JSON schema for use in the converter
 */
const createMockManifestFromSchema = (
  schema: ManifestJSONSchema,
): ManifestIntegrationProvider => {
  return {
    icon_path: null,
    name: "mock_converter",
    audience: "internal",
    version: "1.0.0",
    display_name: "Mock Converter",
    description: "Mock converter for JSON schema testing",
    geographic_restrictions: [],
    grouping_categories: [],
    sample_responses: {},
    supports_raw_requests: false,
    is_generic_tool: false,
    environment_configs: {
      production: {
        auth_settings: {
          auth_method: "no_auth",
          ssl_enabled: false,
        },
        base_url: "https://mock.example.com",
        constants: {},
        display_name: "Production",
        inputs: {
          properties: {},
          required: [],
          secrets: [],
          title: "Mock Environment Configuration",
          type: "object",
        },
      },
    },
    resources: {
      mock_resource: {
        display_name: "Mock Resource",
        description: "Mock resource for JSON schema testing",
        geographic_restrictions: [],
        node_inputs: schema,
        insights: {},
        allow_caching: false,
        resource_inputs: {},
      },
    },
  };
};

export type ManifestNodeBuilderWindowData = {
  initialValue: SchemaUI;
  schemaType: SchemaOptions;
  onChange: (value: SchemaUI) => void;
  onClose: () => void;
};

export const ManifestNodeBuilderWindow: React.FC<FloatingWindowProps> = ({
  computePosition,
  data,
}) => {
  const { closeWindow } = useManifestNodeBuilderWindow();
  const { position, windowRef } = useFloatingWindowPosition(computePosition);
  const { initialValue, schemaType, onChange, onClose } =
    data as ManifestNodeBuilderWindowData;

  const [jsonSchema, setJsonSchema] = useState<string>(() => {
    try {
      // Check if there are meaningful properties (not just empty defaults)
      const hasRealProperties = initialValue.properties.some(
        (prop) => prop.fieldName && prop.fieldName.trim() !== "",
      );

      if (hasRealProperties) {
        const backendSchema = SchemaConverter.uiToBE(initialValue, schemaType);
        return JSON.stringify(backendSchema, null, 2);
      }
    } catch (e) {
      logger.error("Failed to convert initial schema:", e);
    }
    return DEFAULT_SCHEMA_EXAMPLE;
  });
  const [inputMappings, setInputMappings] = useState<InputMappingsType | null>(
    null,
  );
  const [baseInputMappings, setBaseInputMappings] =
    useState<InputMappingsType | null>(null);
  const [validationError, setValidationError] =
    useState<ValidationError | null>(null);
  const [isValidating, setIsValidating] = useState(false);

  const handleCloseAllModals = () => {
    closeWindow();
    onClose();
  };

  // Debounced validation and conversion
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsValidating(true);

      const result = parseAndValidateJsonSchema(jsonSchema);

      if (!result.isValid || result.error) {
        setValidationError(result.error);
        setInputMappings(null);
      } else if (result.schema) {
        setValidationError(null);

        const mockManifest = createMockManifestFromSchema(result.schema);
        const mockIntegrationResource = getDefaultManifestProviderResource(
          {
            provider: "mock_converter",
            resource: "mock_resource",
            manifest_version: "1.0.0",
          },
          "converter-mock",
          "converter-mock",
          mockManifest,
        );

        const filledMappings = fillInputMappingFromManifest(
          mockManifest,
          mockIntegrationResource,
        );
        setInputMappings(filledMappings);
        setBaseInputMappings(filledMappings);
      }

      setIsValidating(false);
    }, 500);

    return () => clearTimeout(timer);
  }, [jsonSchema]);

  const displayResource: ManifestIntegrationResource | null =
    React.useMemo(() => {
      if (!inputMappings) return null;

      const mockManifest = createMockManifestFromSchema(JSON.parse(jsonSchema));
      const mockIntegrationResourceFE = getDefaultManifestProviderResource(
        {
          provider: "mock_converter",
          resource: "mock_resource",
          manifest_version: "1.0.0",
        },
        "converter-mock",
        "converter-mock",
        mockManifest,
      );

      mockIntegrationResourceFE["input"] = inputMappings;
      return mockIntegrationResourceFE;
    }, [inputMappings]);

  const handleSchemaChange = (value: string) => {
    setJsonSchema(value);
  };

  const handleUpdateInputMapping = useCallback(
    (updatedInputMappings: InputMappingsType) => {
      if (!baseInputMappings) {
        setInputMappings(updatedInputMappings);
        return;
      }

      const mergedMappings: InputMappingsType = {
        ungrouped: updatedInputMappings.ungrouped,
        multiselectors: updatedInputMappings.multiselectors,
        grouped: Object.fromEntries(
          Object.entries(updatedInputMappings.grouped).map(([key, group]) => [
            key,
            {
              ...group,
              getDefaultElements:
                baseInputMappings.grouped[key]?.getDefaultElements ||
                group.getDefaultElements,
            },
          ]),
        ),
        lists: Object.fromEntries(
          Object.entries(updatedInputMappings.lists).map(([key, list]) => [
            key,
            {
              ...list,
              getDefaultElement:
                baseInputMappings.lists[key]?.getDefaultElement ||
                list.getDefaultElement,
            },
          ]),
        ),
      };

      setInputMappings(mergedMappings);
    },
    [baseInputMappings],
  );

  const handleGenerate = useCallback(() => {
    if (validationError || !jsonSchema) {
      return;
    }

    try {
      const parsed = JSON.parse(jsonSchema);
      const convertedSchema = SchemaConverter.beToUI(parsed, schemaType);
      onChange(convertedSchema);
      handleCloseAllModals();
    } catch (e) {
      // Error is already handled by validation
      logger.error("Failed to generate schema:", e);
    }
  }, [jsonSchema, validationError, schemaType, onChange, handleCloseAllModals]);

  return (
    <FloatingWindowInternal
      ref={windowRef}
      height="h-178"
      isMaximized={true}
      isPinned={false}
      minHeight="h-160"
      minWidth="w-200"
      style={{
        inset: "0px auto auto 0px",
        transform: position,
      }}
      title={<Title />}
      width="w-220"
      draggable
      lockSchemaEditor
      maximizable
      resizeable
      onClose={handleCloseAllModals}
    >
      <div className="flex h-full w-full flex-col overflow-hidden">
        <div className="flex flex-1 overflow-hidden">
          {/* Left panel - JSON Schema Editor */}
          <div className="flex w-3/5 flex-col border-gray-200 border-r">
            <div className="border-gray-200 border-b px-4 py-2">
              <h2 className="font-medium text-gray-900 text-sm">JSON Schema</h2>
              {isValidating && (
                <span className="text-gray-500 text-xs">Validating...</span>
              )}
            </div>
            <div className="flex-1 overflow-hidden p-4">
              {validationError && (
                <div className="mb-2">
                  <Callout variant="error">{validationError.message}</Callout>
                </div>
              )}
              <CodeEditor
                dataLoc="json-schema-input"
                language="json"
                lineWrapping={true}
                value={jsonSchema}
                onChange={handleSchemaChange}
              />
            </div>
          </div>

          {/* Right panel - UI Preview */}
          <div className="flex w-2/5 flex-col">
            <div className="border-gray-200 border-b px-4 py-2">
              <h2 className="font-medium text-gray-900 text-sm">
                Generated UI Preview
              </h2>
            </div>
            <div className="decideScrollbar flex-1 overflow-y-auto p-4">
              {validationError && <ErrorDisplay error={validationError} />}
              {!validationError && displayResource && (
                <div data-loc="ui-preview-panel">
                  <InputMappings
                    immutable={false}
                    integrationResource={displayResource}
                    isReactive={false}
                    updateInputMapping={handleUpdateInputMapping}
                  />
                </div>
              )}
              {!validationError && !displayResource && (
                <div className="flex h-full items-center justify-center text-gray-500">
                  <div className="text-center">
                    <p className="text-sm">No valid schema to display</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer with Generate button */}
        <div className="flex justify-end border-gray-200 border-t bg-gray-50 p-4">
          <Button
            dataLoc="manifest-node-builder-generate"
            isDisabled={!!validationError || !jsonSchema}
            variant="primary"
            onClick={handleGenerate}
          >
            Generate
          </Button>
        </div>
      </div>
    </FloatingWindowInternal>
  );
};
